import { translate, TranslateError } from 'magner';
import {
  MembersImport, MembersImportResult, MembersImportSchema, MembersImportTemplateResult,
} from 'features/residents/types/members-import';
import { request } from '~/utils/request';

interface MembersImportResponse {
  import: MembersImport,
}

interface MembersImportErrorBody {
  result?: {
    type?: string,
    message?: string,
    errors?: {
      root?: string[],
      fields?: Record<string, string>,
    },
  },
}

/** Подробные логи только в dev-сборке, предупреждения и ошибки — всегда */
const logInfo = (method: string, message: string, data?: unknown) => {
  if (import.meta.env.DEV) {
    console.info(`[MembersImport.${method}] ${message}`, data ?? '');
  }
};

/**
 * Запросы идут через api.instance (как в gaz-импорте), поэтому глобальный interceptError
 * не показывает тосты — все ошибки выводятся внутри диалога импорта.
 */
const parseImportError = (method: string, e: { status?: number, text?: string }): MembersImportResult => {
  // Вызывается из .catch запроса, вне setup компонента: useTranslate() → useI18n() там бросает исключение.
  // TranslateError переводит через глобальный t Magner (как в features/common/file-request.ts).
  const fallback = new TranslateError(translate('hubstr.resident.members_import.error')).name;

  let body: MembersImportErrorBody = {};
  try {
    body = JSON.parse(e?.text ?? '{}');
  } catch (parseErr) {
    console.error(`[MembersImport.${method}] unparsable error response`, { status: e?.status, parseErr });
  }

  const fields = Object.values(body.result?.errors?.fields ?? {});
  const root = body.result?.errors?.root ?? [];
  const message = [...fields, ...root].join(' ') || body.result?.message || fallback;

  console.warn(`[MembersImport.${method}] request failed`, {
    status: e?.status,
    type: body.result?.type,
    message,
  });

  return { error: message, status: e?.status };
};

const handleResponse = (method: string) => (response: unknown): MembersImportResult => {
  const data = (response as MembersImportResponse)?.import;
  if (!data) {
    console.error(`[MembersImport.${method}] unexpected response`, response);
    return { error: 'Некорректный ответ сервера' };
  }

  logInfo(method, 'ok', {
    id: data.id,
    status: data.status,
    totalRows: data.totalRows,
    validCount: data.validCount,
    invalidCount: data.invalidCount,
    createdCount: data.createdCount,
    notImportedCount: data.notImportedCount,
  });

  return { data };
};

/**
 * Пустой .xlsx-шаблон по раскладке клуба (members_import.schema).
 * Ответ — файл, а не JSON; при выключенной фиче приходит 404 в обычном JSON-формате ошибки.
 */
export const membersImportTemplate = (): Promise<MembersImportTemplateResult> => {
  logInfo('template', '[FIX] start');

  const template = request.custom<MembersImportTemplateResult>(async ({ api }) => await api
    .instance
    .url('/api/admin/members-import/template')
    .get()
    .blob((blob: Blob) => {
      logInfo('template', '[FIX] ok', { size: blob.size, type: blob.type });
      return { blob };
    })
    .catch((e) => parseImportError('template', e)) as any);

  return template({}) as unknown as Promise<MembersImportTemplateResult>;
};

/** Раскладку бэк берёт из настройки клуба members_import.schema, schema передаём только в лог */
export const membersImportValidate = (file: File, schema: MembersImportSchema): Promise<MembersImportResult> => {
  logInfo('validate', 'start', { schema, fileName: file?.name, size: file?.size });

  const validate = request.custom<MembersImportResult>(async ({ api }) => await api
    .instance
    .url('/api/admin/members-import/validate')
    .formData({ file })
    .post()
    .json(handleResponse('validate'))
    .catch((e) => parseImportError('validate', e)) as any);

  return validate({}) as unknown as Promise<MembersImportResult>;
};

export const membersImportStart = (id: string): Promise<MembersImportResult> => {
  logInfo('start', 'start', { id });

  const start = request.custom<MembersImportResult>(async ({ api }) => await api
    .instance
    .url('/api/admin/members-import/start')
    .post({ id })
    .json(handleResponse('start'))
    .catch((e) => parseImportError('start', e)) as any);

  return start({}) as unknown as Promise<MembersImportResult>;
};

export const membersImportGet = (id: string): Promise<MembersImportResult> => {
  logInfo('get', 'start', { id });

  const get = request.custom<MembersImportResult>(async ({ api }) => await api
    .instance
    .url(`/api/admin/members-import/get?id=${encodeURIComponent(id)}`)
    .get()
    .json(handleResponse('get'))
    .catch((e) => parseImportError('get', e)) as any);

  return get({}) as unknown as Promise<MembersImportResult>;
};
